import React, { useCallback, useEffect, useRef } from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'

function formatSize(bytes) {
  if (!bytes) return '0 MB'
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default withTranslation()(function CreativeUploadStep({ t, file, setFile }) {
  const inputRef = useRef(null)

  useEffect(() => {
    if (!file || file.progress >= 100) return undefined

    const interval = setInterval(() => {
      setFile((prev) => {
        if (!prev || prev.progress >= 100) return prev
        return { ...prev, progress: Math.min(100, prev.progress + 12) }
      })
    }, 250)

    return () => clearInterval(interval)
  }, [file, setFile])

  const handleFiles = useCallback((fileList) => {
    const selected = fileList?.[0]
    if (!selected) return
    setFile({ name: selected.name, size: selected.size, progress: 0, rawFile: selected })
  }, [setFile])

  return (
    <div className="flex flex-col gap-6 max-w-[640px]">
      <h2 className="text-xl font-semibold text-dark-blue">{t('rewardAds.createFlow.creative.title')}</h2>

      {
        !file ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => { event.preventDefault(); handleFiles(event.dataTransfer.files) }}
            className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border-lines rounded-xl py-14 text-center hover:border-primary"
          >
            <UIcons.UilCloudUpload size={28} className="text-secondary" />
            <p className="text-sm font-medium text-dark-blue">{t('rewardAds.createFlow.creative.dropzone_label')}</p>
            <p className="text-xs text-secondary">{t('rewardAds.createFlow.creative.dropzone_hint')}</p>

            <input
              ref={inputRef}
              type="file"
              accept="video/*,image/*"
              className="hidden"
              onChange={(event) => handleFiles(event.target.files)}
            />
          </button>
        ) : (
          <div className="flex items-center gap-3 border border-dashed border-primary bg-focus rounded-xl p-4">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
              {
                file.progress < 100
                  ? <UIcons.UilSpinner size={20} className="text-primary animate-spin" />
                  : <UIcons.UilCheckCircle size={20} className="text-success" />
              }
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm text-dark-blue truncate">{file.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-secondary whitespace-nowrap">
                  {formatSize(file.size * (file.progress / 100))} / {formatSize(file.size)}
                </p>
                <div className="flex-1 h-1.5 rounded-full bg-white overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${file.progress}%` }} />
                </div>
                <p className="text-xs text-secondary whitespace-nowrap">
                  {file.progress < 100 ? t('rewardAds.createFlow.creative.uploading') : t('rewardAds.createFlow.creative.uploaded')}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setFile(null)}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white shrink-0"
            >
              <UIcons.UilTrashAlt size={16} className="text-danger" />
            </button>
          </div>
        )
      }
    </div>
  )
})
